import { Modal, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import toast from "react-hot-toast";
import {
  IconCart,
  IconEye,
  IconSettings,
  IconShare,
} from "../../../../../../utils/icons";
import { GeneralSettings } from "./GeneralSettings";
import { IEmbed } from "../../../embedsV2/interface";
import { ShopabilitySettings } from "./ShopabilitySettings";
import { ICreateEmbed } from "../constants";
import axiosInstant from "../../../../../../api";
import { createEmbed } from "../../../../../../api/endpoints";
import { useUser } from "../../../../../../hooks/useUser";
import { VisualizationSettings } from "./visualization";
import { TabHeader, TabPanel } from "../../../../../../components/Tabs";
import { ShareEmbedModal } from "../ShareVideoModal";

interface Props {
  embed?: IEmbed;
  onClose: () => void;
  onComplete: () => void;
}

export function StoryForm({ embed, onClose, onComplete }: Props) {
  const { brand } = useUser();
  const newEmbed = useRef<ICreateEmbed>();
  const popupRef = useRef<HTMLDivElement>(null);
  const [panelHeight, setPanelHeight] = useState(0);
  const [selectedTab, setSelectedTab] = useState(0);

  const createStory = async () => {
    if (!newEmbed.current) return;
    const brandId: number = brand ? brand[0].id : 0;
    const { shopability, colors, ...rest } = { ...newEmbed.current, brandId };

    try {
      const payload = {
        ...rest,
        settings: JSON.stringify({ annotations: [shopability], colors }),
      };
      const response = await axiosInstant.post(createEmbed(), payload);
      props.onComplete();

      const { status } = response;
      if (status === 200 || status === 201) {
        toast.success(`Product ${payload.name} has been created.`);
      }
    } catch (error) {
      console.log("Error", error);
      toast.error((error as Error).message);
    }
  };

  const onNewEmbedChange = (data: Partial<ICreateEmbed>) => {
    const prevData = newEmbed.current || {};
    // @ts-ignore
    newEmbed.current = { ...prevData, ...data };
  };

  useEffect(() => {
    setTimeout(() => {
      if (popupRef.current?.clientHeight) {
        setPanelHeight(popupRef.current?.clientHeight);
      }
    }, 200);
  }, [popupRef]);

  const props = {
    embed,
    height: panelHeight - 170,
    onComplete,
    updateEmbed: onNewEmbedChange,
    onCreateStory: createStory,
  };

  const tabHeader = [
    {
      value: 0,
      text: "General",
      Icon: IconSettings,
    },
    {
      value: 1,
      text: "Visualization",
      Icon: IconEye,
    },
    {
      value: 2,
      text: "Shopability",
      Icon: IconCart,
    },
    {
      value: 3,
      text: "Share",
      Icon: IconShare,
    },
  ];

  return (
    <Modal
      open
      className="flex items-center flex-col, justify-center"
      onClose={onClose}
      onClick={(e: any) => {
        e.stopPropagation();
        //e.preventDefault();
      }}
    >
      <div
        className="bg-surface py-6 rounded-xl w-full max-w-[930px] h-full max-h-[745px]"
        ref={popupRef}
      >
        <div className="px-6 flex items-center justify-between">
          <Typography variant="h5">
            {embed ? "Story Settings" : "Create new story"}
          </Typography>
          <CloseIcon onClick={onClose} />
        </div>
        <TabHeader
          tabs={tabHeader}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          containerClassName="p-6 border-b border-border-token"
        />
        <TabPanel value={selectedTab} index={0}>
          <GeneralSettings {...props} />
        </TabPanel>
        <TabPanel value={selectedTab} index={1}>
          <VisualizationSettings {...props} />
        </TabPanel>
        <TabPanel value={selectedTab} index={2}>
          <ShopabilitySettings {...props} />
        </TabPanel>
        <TabPanel value={selectedTab} index={3}>
          <ShareEmbedModal
            embed={embed!}
            openSettings={() => setSelectedTab(0)}
            panelHeight={panelHeight}
          />
        </TabPanel>
      </div>
    </Modal>
  );
}
